import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import FirmsTable from '../components/FirmsTable';
import CreateFirmForm from '../components/CreateFirmForm';
import firmService from '../services/firmService';
import '../styles/firms.css';

const getErrorMessage = (error) =>
  error.response?.data?.message || error.message || 'Something went wrong.';

const FirmsPage = () => {
  const navigate = useNavigate();

  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingFirm, setEditingFirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadFirms = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const firmData = await firmService.getFirms();

      // Match the response handling used in AdminDashboard.js.
      const firmsList = Array.isArray(firmData)
        ? firmData
        : Array.isArray(firmData?.firms)
          ? firmData.firms
          : [];

      setFirms(firmsList);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFirms();
  }, [loadFirms]);

  const filteredFirms = useMemo(() => {
    const term = search.trim().toLowerCase();

    return firms.filter((firm) => {
      const matchesSearch =
        !term ||
        [
          firm.firm_name,
          firm.firm_code,
          firm.owner_name,
          firm.email,
          firm.mobile,
        ].some((value) => String(value || '').toLowerCase().includes(term));

      const matchesStatus =
        statusFilter === 'all' || firm.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [firms, search, statusFilter]);

  const handleSave = async (payload) => {
    setSaving(true);
    setError('');

    try {
      await firmService.updateFirm(editingFirm.id, payload);
      setEditingFirm(null);
      await loadFirms();
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (firm, nextStatus) => {
    const action = nextStatus === 'active' ? 'activate' : 'deactivate';

    if (
      !window.confirm(
        `Are you sure you want to ${action} ${firm.firm_name}?`
      )
    ) {
      return;
    }

    setError('');

    try {
      await firmService.updateFirmStatus(firm.id, nextStatus);
      await loadFirms();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">BUSINESS MANAGEMENT</p>
          <h1>Firms</h1>
          <p>Manage firm information and account status.</p>
        </div>

        <Link
          to="/admin/firms/create"
          className="admin-button admin-button-primary"
        >
          + Create Firm
        </Link>
      </header>

      {error && (
        <div className="admin-alert admin-alert-error" role="alert">
          {error}
        </div>
      )}

      {editingFirm ? (
        <section className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2>Edit Firm</h2>
              <p>{editingFirm.firm_name}</p>
            </div>
          </div>

          <CreateFirmForm
            key={editingFirm.id}
            initialValues={editingFirm}
            onSubmit={handleSave}
            onCancel={() => setEditingFirm(null)}
            submitting={saving}
            submitLabel="Save Changes"
          />
        </section>
      ) : (
        <>
          <section className="admin-filter-bar">
            <div className="admin-search-field">
              <label htmlFor="firm-search">Search firms</label>
              <input
                id="firm-search"
                type="search"
                placeholder="Search by name, code, owner or email..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className="admin-filter-field">
              <label htmlFor="firm-status-filter">Status</label>
              <select
                id="firm-status-filter"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <button
              type="button"
              className="admin-button admin-button-secondary"
              onClick={loadFirms}
              disabled={loading}
            >
              Refresh
            </button>
          </section>

          <section className="admin-section">
            <div className="admin-section-header">
              <div>
                <h2>All Firms</h2>
                <p>{filteredFirms.length} firms found</p>
              </div>
            </div>

            <FirmsTable
              firms={filteredFirms}
              loading={loading}
              onView={(firm) => navigate(`/admin/firms/${firm.id}`)}
              onEdit={(firm) => setEditingFirm(firm)}
              onStatusChange={handleStatusChange}
            />
          </section>
        </>
      )}
    </main>
  );
};

export default FirmsPage;